import { ThreadPrimitive } from '@assistant-ui/react';
import ChatComposer from 'components/Chat/ChatComposer';
import ChatMessages from 'components/Chat/ChatMessages';
import ChatPromptSuggestions from 'components/Chat/ChatPromptSuggestions';
import { useTranslation } from 'react-i18next';
import { Button, Icon, Loading, Stack, styled, Text, Tooltip } from 'shell/components';

const CHAT_PANEL_SPACING = '20px';
const CHAT_BG_COLOR = '#FCFCFC';

const ChatPanelView = ({ composer, error, header, suggestions, thread }) => {
    const { t } = useTranslation('assignment');

    return (
        <RootStack>
            <HeaderRow>
                {header.showBackButton ? (
                    <BackButton startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={header.onBack}>
                        {t('common:controls.back', 'Back')}
                    </BackButton>
                ) : null}
                <CenteredTitleRow
                    alignItems={'center'}
                    direction={'row'}
                    isTitleCentered={header.isTitleCentered}
                    spacing={1}
                >
                    <Text component={'h2'} sx={{ textAlign: 'center' }} weight={'semibold'}>
                        {header.title}
                    </Text>
                    {header.infoTooltip ? <Tooltip content={header.infoTooltip} type={'info'} /> : null}
                </CenteredTitleRow>
            </HeaderRow>

            <ThreadViewportShell hasTopScrollGradient={thread.showTopScrollGradient ? 1 : 0}>
                <ThreadViewport {...thread.viewportProps} onScroll={thread.onScroll}>
                    {thread.isLoadingOlderMessages ? (
                        <ViewportTopStatus
                            alignItems={'center'}
                            direction={'row'}
                            justifyContent={'center'}
                            spacing={1}
                        >
                            <ViewportLoadingSpinner aria-hidden={'true'} />
                            <ViewportLoadingText variant={'p3'}>
                                {t('chat.loading-older', 'Loading Older Messages...')}
                            </ViewportLoadingText>
                        </ViewportTopStatus>
                    ) : null}
                    {thread.shouldDelayContent ? <Loading /> : <ChatMessages {...thread.messagesProps} />}
                </ThreadViewport>
                {suggestions.isVisible ? (
                    <SuggestionsOverlay ref={suggestions.overlayRef}>
                        <SuggestionsContainer>
                            <ChatPromptSuggestions
                                disabled={suggestions.disabled}
                                suggestions={suggestions.items}
                                onSelect={suggestions.onSelect}
                            />
                        </SuggestionsContainer>
                    </SuggestionsOverlay>
                ) : null}
            </ThreadViewportShell>

            {composer.isVisible ? (
                <ComposerDock>
                    {error.message ? (
                        <ChatErrorBanner spacing={5}>
                            <Text color={'error'}>{error.message}</Text>
                            {error.action ? (
                                <Button
                                    startIcon={error.action.icon ? <Icon kind={error.action.icon} /> : null}
                                    variant={'secondary'}
                                    onClick={error.onAction}
                                >
                                    {error.action.label}
                                </Button>
                            ) : null}
                        </ChatErrorBanner>
                    ) : null}
                    <ChatComposer {...composer.props} />
                </ComposerDock>
            ) : null}
        </RootStack>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        flex: 1,
        padding: CHAT_PANEL_SPACING,
        background: CHAT_BG_COLOR,
        minHeight: 0,
        fontSize: '14px',

        '& .MuiButton-root': {
            fontSize: '14px',
        },
        '& .MuiInputBase-input, & textarea': {
            fontSize: '14px',
        },
    };
});

const HeaderRow = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        minHeight: theme.spacing(5),
        justifyContent: 'center',
        paddingBottom: theme.spacing(3),
    };
});

const BackButton = styled(Button)(() => {
    return {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
    };
});

const CenteredTitleRow = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'isTitleCentered',
})(({ theme, isTitleCentered }) => {
    return {
        width: '100%',
        flexWrap: 'nowrap',
        padding: isTitleCentered ? theme.spacing(0, 10) : 0,
        justifyContent: isTitleCentered ? 'center' : 'flex-start',
    };
});

const ThreadViewport = styled(ThreadPrimitive.Viewport)(({ theme }) => {
    return {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        marginRight: `-${CHAT_PANEL_SPACING}`,
        paddingRight: CHAT_PANEL_SPACING,
    };
});

const ThreadViewportShell = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'hasTopScrollGradient',
})(({ hasTopScrollGradient }) => {
    return {
        position: 'relative',
        flex: 1,
        minHeight: 0,

        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '48px',
            pointerEvents: 'none',
            opacity: hasTopScrollGradient ? 1 : 0,
            transition: 'opacity 160ms ease',
            background: `linear-gradient(180deg, ${CHAT_BG_COLOR} 0%, rgba(252, 252, 252, 0) 100%)`,
            zIndex: 1,
        },
    };
});

const ViewportTopStatus = styled(Stack)(({ theme }) => {
    return {
        position: 'sticky',
        top: 4,
        zIndex: 1,
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        background: CHAT_BG_COLOR,
    };
});

const ViewportLoadingText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.primary[500],
    };
});

const ViewportLoadingSpinner = styled('span')(({ theme }) => {
    return {
        width: theme.spacing(2),
        height: theme.spacing(2),
        borderRadius: '50%',
        border: `2px solid ${theme.palette.ensightful.primary[100]}`,
        borderTopColor: theme.palette.ensightful.primary[500],
        animation: 'chat-loading-spin 0.8s linear infinite',

        '@keyframes chat-loading-spin': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    };
});

const ComposerDock = styled(Stack)(({ theme }) => {
    return {
        gap: theme.spacing(5),
    };
});

const ChatErrorBanner = styled(Stack)(({ theme }) => {
    return {
        alignItems: 'flex-start',
        padding: theme.spacing(2),
    };
});

const SuggestionsContainer = styled(Stack)(() => {
    return {
        width: '100%',
        alignItems: 'flex-end',
    };
});

const SuggestionsOverlay = styled('div')(({ theme }) => {
    return {
        position: 'absolute',
        right: CHAT_PANEL_SPACING,
        bottom: 0,
        left: 0,
        paddingLeft: CHAT_PANEL_SPACING,
        paddingBottom: theme.spacing(2.5),
        pointerEvents: 'none',

        '& > *': {
            pointerEvents: 'auto',
            width: 'fit-content',
            marginLeft: 'auto',
        },
    };
});

export default ChatPanelView;
